import React, { useState, useEffect } from 'react';
import { getGalleryBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function Gallery() {
  const [galleries, setGalleries] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    async function loadGalleries() {
      const [broed, kager, bageri, butik] = await Promise.all([
        getGalleryBySlug('broed'),
        getGalleryBySlug('kager'),
        getGalleryBySlug('bageri'),
        getGalleryBySlug('butik')
      ]);

      setGalleries({
        broed: broed?.images || [],
        kager: kager?.images || [],
        bageri: bageri?.images || [],
        butik: butik?.images || []
      });
      setLoading(false);
    }
    loadGalleries();
  }, []);

  useSEO({
    title: 'Galleri · Vissenbjerg Bageri',
    description: 'Se billeder af vores lækre surdejsbrød, stenovnsbrød, kager, brunsvigere og butikken i Vissenbjerg. Bliv fristet af vores bagerhåndværk.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  // Saml alle billeder med kategori-tag
  const allImages = [
    ...galleries.broed.map(img => ({ url: img, category: 'broed', categoryLabel: 'Brød & morgenbrød' })),
    ...galleries.kager.map(img => ({ url: img, category: 'kager', categoryLabel: 'Kager & konditori' })),
    ...galleries.bageri.map(img => ({ url: img, category: 'bageri', categoryLabel: 'Bageriet' })),
    ...galleries.butik.map(img => ({ url: img, category: 'butik', categoryLabel: 'Butikken' }))
  ];

  const filteredImages = activeCategory === 'alle'
    ? allImages
    : allImages.filter(img => img.category === activeCategory);

  const categories = [
    { id: 'alle', label: 'Vis alle' },
    { id: 'broed', label: 'Brød & morgenbrød' },
    { id: 'kager', label: 'Kager & konditori' },
    { id: 'bageri', label: 'Bageriet' },
    { id: 'butik', label: 'Butikken' }
  ];

  const handlePrevImage = (e) => {
    e.stopPropagation();
    const currentIndex = filteredImages.findIndex(img => img.url === activeImage);
    if (currentIndex > 0) {
      setActiveImage(filteredImages[currentIndex - 1].url);
    } else {
      setActiveImage(filteredImages[filteredImages.length - 1].url);
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    const currentIndex = filteredImages.findIndex(img => img.url === activeImage);
    if (currentIndex < filteredImages.length - 1) {
      setActiveImage(filteredImages[currentIndex + 1].url);
    } else {
      setActiveImage(filteredImages[0].url);
    }
  };

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Billedgallerier
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Se vores lækre udvalg
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
          <p className="text-sm text-chocolate/70 max-w-xl mx-auto pt-2">
            Gå på opdagelse i vores billedgallerier og lad dig friste af vores friskbagte surdejsbrød og stenovnsbrød, smukke konditorkager, klassiske fynske brunsvigere og stemningen fra bageriet og butikken.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-200 cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-gold-brown text-cream shadow-md'
                  : 'bg-white text-chocolate/80 hover:bg-chocolate/5 hover:text-chocolate border border-gold-brown/10'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((img, idx) => (
            <div
              key={idx}
              onClick={() => setActiveImage(img.url)}
              className="group relative h-64 sm:h-80 md:h-96 rounded-xl overflow-hidden shadow-md border border-gold-brown/10 cursor-pointer bg-chocolate/5"
            >
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=800,fit=cover,quality=85,format=auto/${img.url.replace('https://assets.auxodev.dk/', '')}`}
                alt={img.categoryLabel}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-chocolate/80 via-chocolate/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <span className="text-xs text-gold-brown font-semibold uppercase tracking-wider">
                  {img.categoryLabel}
                </span>
                <span className="text-cream text-xs flex items-center gap-1.5 mt-1">
                  <i className="fa-solid fa-magnifying-glass-plus" /> Se i stor visning
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {activeImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-chocolate/95 backdrop-blur-sm p-4"
            onClick={() => setActiveImage(null)}
          >
            <button
              onClick={() => setActiveImage(null)}
              className="absolute top-6 right-6 text-cream/80 hover:text-cream text-3xl focus:outline-none cursor-pointer"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark" />
            </button>

            <button
              onClick={handlePrevImage}
              className="absolute left-4 md:left-8 text-cream/80 hover:text-cream text-4xl focus:outline-none cursor-pointer p-2 rounded-full bg-cream/5 hover:bg-cream/10"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>

            <div className="max-w-4xl max-h-[80vh] flex items-center justify-center">
              <img
                src={activeImage}
                alt="Stor visning"
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border border-cream/10"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <button
              onClick={handleNextImage}
              className="absolute right-4 md:right-8 text-cream/80 hover:text-cream text-4xl focus:outline-none cursor-pointer p-2 rounded-full bg-cream/5 hover:bg-cream/10"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
